import { useContext, useState, useEffect, useMemo } from 'react';
import styled, { withTheme, css } from 'styled-components';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import Currency from '/util/Currency.js';
import ArticleConfigurationContext from '../../Context/ArticleConfigurationContext';
import RestaurantContext from '../../Context/RestaurantContext';
import cartHelpers from '../Cart/cartHelpers';
import CartContext from '../../Context/CartContext';
import {
  isDtControlledAvailable,
  parseDayArrToString,
  parseSecondsToTime,
} from '../../util/dayHelpers';
import { toast } from 'react-toastify';

const ComponentWrapper = styled.div`
  display: flex;
  flex-flow: column;
  background-color: white;
  min-height: 100px;
  overflow: visible;
  padding: 15px;
  cursor: ${(props) => (props.$isAvailable ? 'pointer' : 'not-allowed')};
  user-select: none !important;

  border-radius: 4px;
  border: 1px solid rgba(120, 130, 140, 0.05);
  box-shadow: 0 2px 9px rgba(120, 130, 140, 0.13);

  ${(props) =>
    props.selected &&
    css`
      ${ArticleTitle} {
        h3 {
          color: ${(props) => props.theme.colors.primary};
        }
      }
      &&& {
        box-shadow: 0 0 0 0.25rem rgba(47, 159, 47, 0.25);
        border-color: rgba(47, 159, 47, 0.4);
      }
    `};

  &:hover {
    box-shadow: 0 2px 9px rgba(120, 130, 140, 0.25);
  }

  transition: box-shadow 0.1s ease-in;

  &.highlightable {
    transition: all 400ms ease-out;
    h3 {
      transition: color 400ms ease-out;
    }
    ${(props) =>
      props.$animate &&
      css`
        transition: none;

        ${ArticleTitle} {
          h3 {
            transition: none;
            color: ${(props) => props.theme.colors.primary};
          }
        }
        box-shadow: 0 0 0 0.25rem rgba(47, 159, 47, 0.25);
        border-color: rgba(47, 159, 47, 0.4);
      `};
  }

  ${(props) => {
    if (!props.$isAvailable) {
      return css`
        ${PricAndTitleWrapper} {
          opacity: ${(props) => (props.$isAvailable ? 1 : 0.5)};
        }
        ${DescriptionWrapper} {
          opacity: ${(props) => (props.$isAvailable ? 1 : 0.5)};
        }
      `;
    }
  }}
`;

const PricAndTitleWrapper = styled.div`
  display: flex;
  flex-flow: row;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: baseline;
`;

const ArticleTitle = styled.div`
  justify-self: flex-start;
  flex-grow: 1;
  word-break: break-word;

  h3 {
    font-size: 17px;
    letter-spacing: 0.8px;
    color: #000000;
    margin: 0;
  }

  margin-bottom: 9px;
`;

const ArticlePrice = styled.div`
  justify-self: flex-end;
  white-space: nowrap;
  span {
  }
`;

const DescriptionWrapper = styled.div`
  margin-bottom: 9px;
  flex-grow: 1;
  p {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.57px;
    color: rgb(105, 105, 105);
    margin: 0;
  }
`;

const InformationLineWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: flex-end;
`;

const AllergeneAndInfoIconWrapper = styled.div`
  justify-content: flex-start;
  display: flex;
  flex-grow: 1;
`;

const InfoIcon = styled.div`
  cursor: pointer;
  display: flex;
  margin-right: 10px;
  align-items: flex-end;

  &:hover {
    #info-icon-svg {
      opacity: 1;
    }
  }

  #info-icon-svg {
    height: 17px;
    opacity: 0.4;
    transition: opacity 0.1s ease-in-out;
  }
`;

const NotAvailableWrapper = styled.div`
  align-items: flex-end;
  justify-content: flex-end;
  text-align: right;
  display: flex;
  flex-grow: 1;

  span {
    font-size: 12px;
    letter-spacing: 0.57px;
    font-weight: 400;
    color: #ec971f;
  }
`;

const AddToCartIconWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-grow: 0;
`;
const IconWrapper = styled.div`
  display: flex;
  margin-right: 10px;

  align-items: flex-end;
`;

function ArticleComponent({ article, isPGAvailable, onNutritionClick, ...props }) {
  const {
    article: articleFromContext,
    setArticleWithConfiguration,
    resetArticleSelection,
  } = useContext(ArticleConfigurationContext);
  const { fetchCart, loadingCart } = useContext(CartContext);
  const { restaurantProps } = useContext(RestaurantContext);

  const isSelectedArticle = () => {
    return articleFromContext?._id === article._id;
  };

  const [isSelected, setIsSelected] = useState(isSelectedArticle);
  const [animateAddEffect, setAnimateAddEffect] = useState(false);
  const [isAvailable, setIsAvailable] = useState(isPGAvailable && isDtControlledAvailable(article));

  useEffect(() => {
    setIsAvailable(isPGAvailable && isDtControlledAvailable(article));
  }, [article, isPGAvailable]);

  useEffect(() => {
    if (isSelectedArticle() && !isSelected) return setIsSelected(true);
    else if (!isSelectedArticle() && isSelected) return setIsSelected(false);
  }, [articleFromContext, isSelected]);

  const isConfigurable = () =>
    (article.extras && article.extras.length > 0) ||
    (article.attributes && article.attributes.length > 0) ||
    (article.menuoptions && article.menuoptions.length > 0) ||
    (article.specialRequests && article.specialRequests.length > 0);

  const articleClicked = () => {
    if (!isAvailable) return false;

    if (isConfigurable()) {
      if (isSelected) {
        resetArticleSelection();
      } else {
        setArticleWithConfiguration(article, undefined);
      }
    } else {
      if (loadingCart) return;
      resetArticleSelection();
      // add directly to cart, since no config needed

      addNonConfigurableArticleToCart();
    }
  };

  const addNonConfigurableArticleToCart = () => {
    // trigger animation
    setAnimateAddEffect(true);
    cartHelpers
      .addArticleToCart(undefined, article, undefined, restaurantProps.restaurantId)
      .then(() => {
        fetchCart();
      })
      .catch(() => {
        toast.error(`Es ist ein Fehler aufgetreten. Das tut uns leid.`, {
          theme: 'colored',
        });
        // todo: hier error in logger
      })
      .finally(() => {
        setAnimateAddEffect(false);
      });
  };

  const showNutrition = (event) => {
    onNutritionClick({
      additives: article.additives,
      allergenes: article.allergenes,
      vegan: article.vegan,
      vegetarian: article.vegetarian,
      hot: article.hot,
      title: article.title,
      _id: article._id,
    });
    event.stopPropagation();
  };

  const hasNutritionInfo = article.additives?.length > 0 || article.allergenes?.length > 0;

  return useMemo(
    () => (
      <ComponentWrapper
        selected={isSelected}
        onClick={articleClicked}
        className={isConfigurable() ? '' : 'highlightable'}
        $animate={animateAddEffect}
        $isAvailable={isAvailable}
      >
        <PricAndTitleWrapper>
          <ArticleTitle>
            <h3>{article.title}</h3>
          </ArticleTitle>
          <ArticlePrice>
            {isConfigurable() ? <span>ab </span> : ''}
            <Currency as="span">{article.price}</Currency>
          </ArticlePrice>
        </PricAndTitleWrapper>

        <DescriptionWrapper>
          <p>{article.description}</p>
        </DescriptionWrapper>

        <InformationLineWrapper>
          <AllergeneAndInfoIconWrapper>
            {hasNutritionInfo && (
              <InfoIcon title="Allergene &amp; Zusatzstoffe" onClick={showNutrition}>
                <Icon name="info" id="info-icon-svg" />
              </InfoIcon>
            )}

            {article.vegetarian && (
              <IconWrapper title="Vegetarisch">
                <Icon style={{ height: 17 }} name="vegetarian" />
              </IconWrapper>
            )}
            {article.vegan && (
              <IconWrapper title="Vegan">
                <Icon style={{ height: 17 }} name="vegan" />
              </IconWrapper>
            )}
            {article.hot && (
              <IconWrapper title="Scharf">
                <Icon style={{ height: 17 }} name="hot" />
              </IconWrapper>
            )}
          </AllergeneAndInfoIconWrapper>
          {isAvailable ? (
            <AddToCartIconWrapper>
              <Icon style={{ width: 17, fill: props.theme.colors.primary }} name="add-circle" />
            </AddToCartIconWrapper>
          ) : (
            <NotAvailableWrapper>
              {isPGAvailable && (
                <span>
                  Nur {parseDayArrToString(article.activeDays)}{' '}
                  {article.timeControlled && (
                    <span>
                      {parseSecondsToTime(article.timeFrom, false)} bis{' '}
                      {parseSecondsToTime(article.timeUntil, false)} Uhr
                    </span>
                  )}
                </span>
              )}
            </NotAvailableWrapper>
          )}
        </InformationLineWrapper>
      </ComponentWrapper>
    ),
    [isSelected, article, animateAddEffect, loadingCart],
  );
}

ArticleComponent.propTypes = {
  article: PropTypes.object.isRequired,
  isPGAvailable: PropTypes.bool,
  onNutritionClick: PropTypes.func.isRequired,
};

export default withTheme(ArticleComponent);
